<template>
  <div class="ops-chart" :style="{ height: `${height}px` }">
    <canvas ref="canvas" class="ops-chart__canvas" role="img" :aria-label="label"></canvas>
  </div>
</template>

<script>
/**
 * One Chart.js 2.8 chart of the Practice Ops report (config from utils/practiceOps/reportCharts.js).
 *
 * The destroy-before-redraw pattern of TeamInsights/components/ChartJSBarChart.vue (pinned there by tests/component/ChartJSBarChart.spec.js):
 *   - ONE live chart per component: a new `config` destroys the old chart before the next is drawn, and unmounting destroys the last
 *     one (a chart left alive keeps its resize listener, its data and its canvas, and a redraw stacks another on top);
 *   - chart.js 2.x writes into the data it is handed (`_meta` on every dataset, patched push / pop / splice on every `data` array), so
 *     it gets a COPY of `config.data` and the Vue-reactive prop is never touched;
 *   - a redraw queued on nextTick can run after the component is gone: it checks first.
 * The chart instance is held outside `data` on purpose (never made reactive).
 */
import { Line, Bar } from 'chart.js';

function cloneChartData(value) {
  if (Array.isArray(value)) return value.map(cloneChartData);
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {};
    Object.keys(value).forEach((k) => { out[k] = cloneChartData(value[k]); });
    return out;
  }
  return value;
}

export default {
  name: 'OpsReportChart',

  props: {
    /** { type: 'line' | 'bar', data, options, plugins } — reportCharts.js waitLineConfig / chairBarsConfig. */
    config: { type: Object, required: true },
    height: { type: Number, default: 220 },
    /** The canvas's text alternative (the table view is the full twin). */
    label: { type: String, default: '' },
  },

  watch: {
    config: {
      handler() {
        this.$nextTick(() => this.createChart());
      },
      deep: true,
    },
  },

  created() {
    this.chart = null;
  },

  mounted() {
    this.createChart();
  },

  beforeDestroy() {
    this.destroyChart();
  },

  methods: {
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },

    createChart() {
      if (this._isDestroyed || !this.$refs.canvas) return;
      const cfg = this.config;
      if (!cfg || !cfg.data || !Array.isArray(cfg.data.datasets)) return;
      this.destroyChart();
      const ctx = this.$refs.canvas.getContext('2d');
      if (!ctx) return;
      const Kind = cfg.type === 'bar' ? Bar : Line;
      this.chart = new Kind(ctx, {
        type: cfg.type === 'bar' ? 'bar' : 'line',
        data: cloneChartData(cfg.data),
        options: cloneChartData(cfg.options || {}),
        plugins: Array.isArray(cfg.plugins) ? cfg.plugins : [],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.ops-chart {
  position: relative;
  width: 100%;
  min-width: 0;
}

.ops-chart__canvas {
  width: 100% !important;
  height: 100% !important;
}
</style>
